import { formations } from "shared/utils/formation.js";
import { getCalculatedOverall } from "shared/utils/player.js";

export function getSquadWarnings(squad, teams = []) {
  if (!squad) return [];

  const warnings = [];
  const players = (squad.teamMembers || []).filter((tm) => tm.player != null).map((tm) => tm.player);

  players.forEach((player) => {
    const otherTeams = teams.filter(
      (team) => team.id !== squad.id && team.teamMembers?.some((tm) => tm.player?.id === player.id),
    );
    if (otherTeams.length > 0) {
      warnings.push(
        `${player.firstName} ${player.lastName} is also in: ${otherTeams.map((team) => team.name).join(", ")}`,
      );
    }
  });

  if (!squad.formation) return warnings;

  const formation = squad.formation;
  const onFieldPlayers = squad.teamMembers.filter(
    (tm) => tm?.player && tm.position !== null && tm.position !== undefined,
  );
  const goalkeepers = players.filter((p) => p.positions.includes("GK"));

  // Check if we have enough players for the formation
  const requiredPositions = formations[formation];
  const requiredPlayerCount = Object.keys(requiredPositions).length;
  const positionCounts = {};
  onFieldPlayers.forEach((tm) => {
    const position = requiredPositions[tm.position]?.position;
    if (position) {
      positionCounts[position] = (positionCounts[position] || 0) + 1;
    }
  });

  if (onFieldPlayers.length < requiredPlayerCount) {
    warnings.push(`Not enough players on field (${onFieldPlayers.length}/${requiredPlayerCount})`);
  }
  if (players.length < 17) {
    warnings.push(`Small squad size (${players.length}/17)`);
  }

  // Check each position in formation
  const positionRequirements = {};
  Object.values(requiredPositions).forEach((posData) => {
    const position = posData.position;
    positionRequirements[position] = (positionRequirements[position] || 0) + 1;
  });

  Object.entries(positionRequirements).forEach(([position, requiredCount]) => {
    // Get all players who can play this position (including starters)
    const positionPlayers = players.filter((p) => p.positions.includes(position));

    // Get starters in this position
    const starters = onFieldPlayers.filter((tm) => requiredPositions[tm.position]?.position === position);

    // Get backups (players who can play this position but aren't starters)
    const backups = positionPlayers.filter((p) => !starters.some((tm) => tm.player.id === p.id));

    // Check if we have enough quality players for this position
    const qualityPlayers = positionPlayers.filter((p) => {
      const calculatedOVR = getCalculatedOverall(p, position);
      return p.overall - calculatedOVR <= 3; // Not more than 3 OVR points worse
    });

    const totalQualityPlayers = qualityPlayers.length;
    const requiredQualityPlayers = requiredCount + 1; // Need X+1 quality players per position

    if (totalQualityPlayers < requiredQualityPlayers) {
      warnings.push(
        `Not enough quality ${position}s (${totalQualityPlayers}/${requiredQualityPlayers} needed). ` +
          `Need ${requiredCount} starter(s) + 1 quality backup`,
      );
    }
  });

  // Check for players with significantly lower calculated OVR in starting 11
  onFieldPlayers.forEach((tm) => {
    const player = tm.player;
    const position = requiredPositions[tm.position]?.position;
    if (position) {
      const calculatedOVR = getCalculatedOverall(player, position);
      const baseOVR = player.overall;
      const difference = baseOVR - calculatedOVR;

      if (difference >= 5) {
        warnings.push(
          `${player.firstName} ${player.lastName} (${position}) has significantly lower OVR in position (${calculatedOVR} vs ${baseOVR})`,
        );
      }
    }
  });

  // Check for retired players
  const retiringSoon = players.filter((p) => p.retirementYears !== null && p.retirementYears < 1);
  if (retiringSoon.length > 0) {
    warnings.push(`${retiringSoon.length} retired player(s) in the squad`);
  }

  // Check overall distribution
  const overallValues = players.map((p) => p.overall);
  const avgOverall = overallValues.reduce((a, b) => a + b, 0) / overallValues.length;
  const minOverall = Math.min(...overallValues);
  const maxOverall = Math.max(...overallValues);

  if (maxOverall - minOverall > 20) {
    warnings.push(`Large overall range (${minOverall}-${maxOverall})`);
  }

  return warnings;
}

export const calculateTeamOverall = (formation, players, includeFullPlayer = false) => {
  const formationPositions = formations[formation];
  if (!formationPositions) {
    throw new Error(`Formation ${formation} not found.`);
  }

  // Extract the positions required by the formation and their indices
  const requiredPositions = Object.entries(formationPositions).map(([index, pos]) => ({
    index: parseInt(index, 10),
    position: pos.position,
  }));

  // Count how many times each position appears in the formation
  const positionCounts = {};
  requiredPositions.forEach((pos) => {
    positionCounts[pos.position] = (positionCounts[pos.position] || 0) + 1;
  });

  // Separate GK and non-GK players
  const gkPlayers = players.filter((p) => p.player.positions.includes("GK"));
  const nonGkPlayers = players.filter((p) => !p.player.positions.includes("GK"));

  // Reset all positions to null initially
  const modifiedPlayers = players.map((p) => ({ ...p, position: null }));

  // Track assigned players to avoid double-counting
  const assignedPlayerIds = new Set();

  // Assign the best GK first
  let totalOverall = 0;
  const assignedPositions = {};

  // Initialize assignedPositions to track how many of each position are filled
  Object.keys(positionCounts).forEach((pos) => {
    assignedPositions[pos] = 0;
  });

  // Assign the best GK
  const gkPosition = requiredPositions.find((pos) => pos.position === "GK");
  if (gkPosition) {
    let bestGkOverall = -Infinity;
    let bestGkPlayerIndex = -1;

    for (let i = 0; i < gkPlayers.length; i++) {
      const overall = getCalculatedOverall(gkPlayers[i].player, "GK");
      if (overall > bestGkOverall) {
        bestGkOverall = overall;
        bestGkPlayerIndex = i;
      }
    }

    if (bestGkPlayerIndex !== -1) {
      const bestGkPlayer = gkPlayers[bestGkPlayerIndex];
      const playerIndex = modifiedPlayers.findIndex((p) => p.id === bestGkPlayer.id);
      if (playerIndex !== -1) {
        modifiedPlayers[playerIndex].position = gkPosition.index;
        assignedPositions["GK"] = 1; // Mark GK as assigned
        assignedPlayerIds.add(bestGkPlayer.id); // Mark player as assigned
        totalOverall += bestGkOverall;
      }
    }
  }

  // Prepare non-GK players with their possible positions and overall ratings
  const playerOptions = nonGkPlayers.map((player) => {
    const options = player.player.positions
      .filter((pos) => pos !== "GK")
      .map((pos) => ({
        position: pos,
        overall: getCalculatedOverall(player.player, pos),
      }));
    return { player, options, originalIndex: modifiedPlayers.findIndex((p) => p.id === player.id) };
  });

  // Sort non-GK players by their best possible overall (descending)
  playerOptions.sort((a, b) => {
    const bestA = Math.max(...a.options.map((opt) => opt.overall), 0);
    const bestB = Math.max(...b.options.map((opt) => opt.overall), 0);
    return bestB - bestA;
  });

  // Assign non-GK players to positions greedily
  for (const { player, options, originalIndex } of playerOptions) {
    // Skip if the player is already assigned
    if (assignedPlayerIds.has(player.id)) continue;

    // Sort options by overall (descending)
    options.sort((a, b) => b.overall - a.overall);

    // Try to assign the player to their best possible position
    for (const { position, overall } of options) {
      if (assignedPositions[position] < positionCounts[position]) {
        // Find the next available index for this position
        const positionIndices = requiredPositions.filter((pos) => pos.position === position).map((pos) => pos.index);
        const assignedIndices = modifiedPlayers
          .filter((p) => positionIndices.includes(p.position))
          .map((p) => p.position);
        const availableIndices = positionIndices.filter((index) => !assignedIndices.includes(index));

        if (availableIndices.length > 0) {
          modifiedPlayers[originalIndex].position = availableIndices[0];
          assignedPositions[position]++;
          assignedPlayerIds.add(player.id); // Mark player as assigned
          totalOverall += overall;
          break;
        }
      }
    }
  }

  // Fill remaining positions with the best available players (even if not ideal)
  for (const pos of requiredPositions) {
    if (assignedPositions[pos.position] < positionCounts[pos.position]) {
      let bestOverall = -Infinity;
      let bestOriginalIndex = -1;

      // Check all players (including GKs) for this position
      for (const { player, options, originalIndex } of playerOptions) {
        // Skip if the player is already assigned
        if (assignedPlayerIds.has(player.id)) continue;

        const option = options.find((opt) => opt.position === pos.position);
        if (option && option.overall > bestOverall) {
          bestOverall = option.overall;
          bestOriginalIndex = originalIndex;
        }
      }

      // Also check GK players (excluding GK position)
      for (let i = 0; i < gkPlayers.length; i++) {
        const gkPlayer = gkPlayers[i];
        if (assignedPlayerIds.has(gkPlayer.id)) continue; // Skip if already assigned

        if (gkPlayer.player.positions.includes(pos.position) && pos.position !== "GK") {
          const overall = getCalculatedOverall(gkPlayer.player, pos.position);
          if (overall > bestOverall) {
            bestOverall = overall;
            bestOriginalIndex = modifiedPlayers.findIndex((p) => p.id === gkPlayer.id);
          }
        }
      }

      if (bestOriginalIndex !== -1 && bestOverall !== -Infinity) {
        modifiedPlayers[bestOriginalIndex].position = pos.index;
        assignedPositions[pos.position]++;
        assignedPlayerIds.add(modifiedPlayers[bestOriginalIndex].id); // Mark player as assigned
        totalOverall += bestOverall;
      }
    }
  }

  let modifiedPlayers2;

  if (includeFullPlayer) {
    modifiedPlayers2 = modifiedPlayers.map((p) => ({
      id: p.id,
      position: p.position,
      player: p.player,
    }));
  } else {
    modifiedPlayers2 = modifiedPlayers.map((p) => ({
      id: p.id,
      position: p.position,
    }));
  }

  return [totalOverall, modifiedPlayers2];
};
